/**
 * Booking Types - TypeScript Definitionen für Buchungsverwaltung
 * Zentrale Type-Definitionen für alle Booking-Komponenten
 */

// Haupt-Buchungstyp
export interface Booking {
  id: string;
  bookingNumber: string;
  customerId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  tripDate: string;
  tripTime: string;
  tripType: 'Basis Tour' | 'ALL IN' | 'ALL IN Premium' | 'Exklusiv Charter';
  participantCount: number;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  paymentStatus: 'pending' | 'partial' | 'paid' | 'refunded';
  totalAmount: number;
  paidAmount: number;
  notes: string[];
  createdAt: string;
  updatedAt: string;
  extras?: string[];
  specialRequests?: string;
  internalNotes?: string;
}

// Edit-Formular Daten
export interface BookingEditForm {
  participantCount: number;        // Min: 1, Max: 140
  tripDate: string;                // ISO Date
  tripTime: string;                // HH:mm Format
  tripType: 'Basis Tour' | 'ALL IN' | 'ALL IN Premium' | 'Exklusiv Charter';
  extras: string[];                // z.B. ['Bierverkostung', 'Grillpaket']
  specialRequests: string;         // Freitext
  internalNotes: string;           // Admin-only Notizen
}

// Stornierungsformular - gemäß Backend BookingCancellationDto
export interface CancellationForm {
  reason: string;                  // Stornierungsgrund (Pflichtfeld, min 10 Zeichen)
  refundAmount: number;            // Rückerstattung (0 bis paidAmount)
  refundMethod: 'bank' | 'card' | 'paypal' | 'voucher' | 'none'; // T-118 j (v1.8.36): card, paypal
  sendConfirmation: boolean;       // E-Mail an Kunde?
  internalNote: string;            // Admin-Notiz
}

// Backend DTOs - exakte Mappings

export interface BookingCancellationDto {
  reason: string;                  // Min 10 chars, max 1000
  refundAmount: number;
  refundMethod: string;            // 'bank' | 'voucher' | 'none'
  internalNote?: string;
  sendConfirmation: boolean;
}

export interface BookingCancellationResponseDto {
  success: boolean;
  message: string;
  refundedAmount: number;
}

export interface CreateBookingNoteDto {
  text: string;                    // Max 2000 chars
  type: string;                    // 'internal' | 'customer' | 'system'
  isReminder: boolean;
  reminderDate?: string;           // ISO DateTime
}

export interface BookingNoteDto {
  id: number;
  bookingId: number;
  text: string;
  type: string;
  createdBy: string;
  createdAt: string;               // ISO DateTime
  isReminder: boolean;
  reminderDate?: string;
  reminderCompleted: boolean;
}

export interface BookingStatusChangeDto {
  newStatus: string;               // Min 5 chars reason
  reason: string;                  // Max 500 chars
}

export interface BookingUpdateDto {
  numberOfGuests: number;
  mealSelections: MealSelectionDto[];
  selectedExtraIds: number[];
}

export interface MealSelectionDto {
  mealId: string;
  quantity: number;
}

export interface ManualBookingDto {
  eventDateId: string;
  variantId: string;
  numberOfGuests: number;
  guestName: string;
  email: string;
  phone: string;
  adminNote?: string;
}

// Notiz-Struktur
export interface BookingNote {
  id: string;
  bookingId: string;
  text: string;
  createdBy: string;         // Admin-Name
  createdAt: string;         // ISO Timestamp
  type: 'internal' | 'customer' | 'system';
  isReminder: boolean;       // Erinnerung im Dashboard?
  reminderDate?: string;     // Datum für Erinnerung
}

// Manuelle Buchung unter Mindestgrenze
export interface ManualBookingForm {
  customerId: number;
  tripDate: string;
  tripTime: string;
  participantCount: number;  // < 30 erlaubt
  tripType: 'Basis Tour' | 'ALL IN' | 'ALL IN Premium' | 'Exklusiv Charter';
  underMinimumAcknowledged: boolean;
  alternativeDate?: string;
  specialRequests?: string;
  internalNotes?: string;
}

// Status-Workflow Definitionen
export type BookingStatus = 'pending' | 'confirmed' | 'cancelled' | 'completed';

export const STATUS_TRANSITIONS: Record<BookingStatus, BookingStatus[]> = {
  'pending': ['confirmed', 'cancelled'],
  'confirmed': ['completed', 'cancelled'],
  'cancelled': [],
  'completed': []
};

export const STATUS_LABELS: Record<BookingStatus, string> = {
  'pending': 'Ausstehend',
  'confirmed': 'Bestätigt',
  'cancelled': 'Storniert',
  'completed': 'Abgeschlossen'
};

export const STATUS_COLORS: Record<BookingStatus, string> = {
  'pending': 'yellow',
  'confirmed': 'green',
  'cancelled': 'red',
  'completed': 'blue'
};

// Verfügbarkeitsprüfung
export interface AvailabilityCheck {
  date: string;
  participantCount: number;
  isAvailable: boolean;
  availableSpots: number;
  message?: string;
}

// Preisberechnung
export interface PriceCalculation {
  basePrice: number;
  extrasPrice: number;
  totalPrice: number;
  pricePerPerson: number;
  isUnderMinimum: boolean;
  minimumGroupPrice?: number;
}

// Validierungsergebnisse
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

// API Response Types
export interface BookingUpdateResponse {
  success: boolean;
  booking: Booking;
  message: string;
}

export interface CancellationResponse {
  success: boolean;
  message: string;
  refundAmount: number;
}

export interface StatusChangeResponse {
  success: boolean;
  booking: Booking;
  message: string;
}
